import { useCurrentCompany, useCurrentUser } from "hooks/auth";
import { selectCurrentProfileType, selectProfileLoading } from "newui/features/Auth/authSelectors";
import { useAppDispatch, useAppSelector } from "newui/store/hooks";
import {
	createContext,
	useMemo,
	useCallback,
	useState,
	ReactNode,
	useContext,
	useEffect,
} from "react";
import { useSelector } from "react-redux";
import { isProcurerNBidder } from "utils/helpers";
import { setCurrentProfileType, setIsProfileLoading } from "newui/features/Auth/authSlice";
import { CompanyProfileType } from "newui/features/Auth/types/CompanyProfileType";
import { navigate } from "@reach/router";
import { useNavigationPaths } from "../hooks/useNavigationPaths";

export enum CurrentAccountType {
	BIDDER = "Bidder",
	PROCURER = "Procurer"
}

// Define the shape of the context
interface ProfileContextValue {
	currentProfile: CurrentAccountType;
	forceRestartTour: boolean;
	isAccountSwitchable: boolean;
	isClientProfileActive: boolean;
	isRfqFeatureEnabled: boolean;
	/** PRO-637: register the RFx route tree — flag OR panel/approver participation. */
	canAccessRfxRoutes: boolean;
	setForceRestartTour: React.Dispatch<React.SetStateAction<boolean>>;
	userId?: string | number;
	changeProfileType: (profileType: CompanyProfileType) => void;
	resetTour: () => Promise<void>;
}

// Create context with default value
export const ProfileContext = createContext<ProfileContextValue>({
	isClientProfileActive: true,
	currentProfile: CurrentAccountType.PROCURER,
	isAccountSwitchable: false,
	changeProfileType: () => {
		// default do nothing
	},
	isRfqFeatureEnabled: false,
	canAccessRfxRoutes: false,
	userId: undefined,
	forceRestartTour: false,
	setForceRestartTour: () => {
		// default do nothing
	},
	resetTour: async () => {
		// default do nothing
	},
});

// Helper function typing
const getUserAccountType = (profileType: CompanyProfileType) => {
	return {
		currentAccountType: profileType === "client" ? CurrentAccountType.PROCURER : CurrentAccountType.BIDDER,
		isClientProfileActive: profileType === "client",
	};
};

// Props typing
interface ProfileContextProviderProps {
	children: ReactNode;
}

export const ProfileContextProvider = ({ children }: ProfileContextProviderProps) => {
	const role = useAppSelector(selectCurrentProfileType);
	const isProfileLoading = useAppSelector(selectProfileLoading);
	const { getHomePageForProfileType } = useNavigationPaths();
	const dispatch = useAppDispatch();

	const { currentUser } = useCurrentUser();
	const company = useCurrentCompany();

	const userId = currentUser?.id;

	const { currentAccountType, isClientProfileActive } = getUserAccountType(role);

	useEffect(() => {
		if (role && currentUser && currentAccountType && isProfileLoading) {
			dispatch(setIsProfileLoading(false));
		}
	}, [currentUser, currentAccountType, dispatch, isProfileLoading, role]);

	const rfqTotal = useSelector((state: any) => state?.supplier_rfq?.total ?? 0);

	/**
	 * Whether this company may RUN RFx as a procurer. Drives the procurer menu
	 * (`AppMenuClient`) — deliberately left narrow: a user who is merely on
	 * someone else's panel must not be offered "Create RFx".
	 */
	const isRfqFeatureEnabled =
		role === "client" ? company?.enable_rfx_functionality ?? false : true;

	/**
	 * PRO-637: whether the RFx ROUTE TREE should be registered at all.
	 *
	 * `enable_rfx_functionality` says whether this company runs RFx as a procurer.
	 * It says nothing about whether this user was invited onto another company's
	 * evaluation panel — orthogonal concerns. But `Account.js` gated its entire
	 * RFx route block on the flag, so a hybrid account without it fell through to
	 * `<NotFound default />` on its own panel-invitation email link while in
	 * procurer mode (`role === "client"`), and could reach neither the Panel list
	 * nor the read-only preview. It only worked in bidder mode, where the ternary
	 * above short-circuits to `true`.
	 *
	 * `has_rfx_participation` is resolved server-side (`UserResource` /
	 * `ResolvesRfxParticipation`) so the client never infers participation itself.
	 * This widens ROUTING only — every RFx surface remains independently
	 * authorised by RfqPolicy, which is the real boundary.
	 */
	const canAccessRfxRoutes =
		isRfqFeatureEnabled || (currentUser?.has_rfx_participation ?? false);

	const isAccountSwitchable = isProcurerNBidder(company);

	const changeProfileType = useCallback(
		(profileType: CompanyProfileType) => {
			// safely navigate to 'home' page for respective profile
			dispatch(setIsProfileLoading(true));
			navigate(getHomePageForProfileType(profileType), { replace: true });
			dispatch(setCurrentProfileType(profileType));
		},
		[dispatch, getHomePageForProfileType]
	);

	const [forceRestartTour, setForceRestartTour] = useState(false);

	const resetTour = useCallback(async () => {
		setForceRestartTour(true);
	}, []);

	const contextValue = useMemo(
		() => ({
			isClientProfileActive,
			currentProfile: currentAccountType,
			isAccountSwitchable,
			changeProfileType,
			isRfqFeatureEnabled,
			canAccessRfxRoutes,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		}),
		[
			changeProfileType,
			currentAccountType,
			isClientProfileActive,
			isAccountSwitchable,
			isRfqFeatureEnabled,
			canAccessRfxRoutes,
			userId,
			forceRestartTour,
			setForceRestartTour,
			resetTour,
		]
	);

	return <ProfileContext.Provider value={contextValue}>{children}</ProfileContext.Provider>;
};

export const useProfileContext = (): ProfileContextValue => {
	const context = useContext(ProfileContext);
	if (!context) {
		throw new Error("useProfileContext must be used within ProfileContextProvider");
	}
	return context;
};